// Accès aux données des dossiers de subvention (D-112). Tout passe par les règles de la base : le bureau du club
// seul (RLS), jamais un autre compte, jamais anon. Le fichier d'une pièce (RIB…) n'est lu qu'à la demande.
import { supabase } from './supabase';
import type {
  Application, Budget, Campaign, Figure, FigureKey, GrantDoc, GrantProfile, Indicator, Measure, National,
} from '../domain/subventions';
import { measureSpec } from '../domain/subventions';

const db = () => { if (!supabase) throw new Error('Base non configurée'); return supabase; };
async function rows<T>(q: PromiseLike<{ data: T[] | null; error: { message: string } | null }>): Promise<T[]> {
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return data ?? [];
}
async function one<T>(q: PromiseLike<{ data: T | null; error: { message: string } | null }>): Promise<T> {
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return data as T;
}
async function must(q: PromiseLike<{ error: { message: string } | null }>): Promise<void> {
  const { error } = await q;
  if (error) throw new Error(error.message);
}

export const nationals = () => rows<National>(db().from('grant_national').select('*').order('position'));
export const campaigns = (club: string) => rows<Campaign>(db().from('grant_campaigns').select('*').eq('club_id', club).order('deadline'));
export type CampaignDraft = Partial<Omit<Campaign, 'id' | 'created_at' | 'reminded_j30' | 'reminded_j7'>>;
export const saveCampaign = (club: string, id: string | null, d: CampaignDraft) =>
  id ? one<Campaign>(db().from('grant_campaigns').update(d).eq('id', id).select('*').single())
    : one<Campaign>(db().from('grant_campaigns').insert({ club_id: club, ...d }).select('*').single());
/** Une campagne nationale : la date de l'année, « pas pour nous » (une ligne par club). */
export const saveNational = (club: string, key: string, d: CampaignDraft) =>
  one<Campaign>(db().from('grant_campaigns').upsert({ club_id: club, national_key: key, ...d }, { onConflict: 'club_id,national_key' }).select('*').single());
export const removeCampaign = (id: string) => must(db().from('grant_campaigns').delete().eq('id', id));

export const bureauNames = async (club: string) => ((await db().rpc('grant_bureau_names', { p_club: club })).data as string[] | null) ?? [];
/** Les équipes suivies dans Arceau cette saison : le périmètre des chiffres proposés. */
export const followedTeams = (club: string, season: string | null = null) =>
  rows<{ name: string; category: string | null }>(db().rpc('grant_teams', { p_club: club, p_season: season }));
/** La prochaine AG de l'agenda : le PV et la liste des dirigeants seront à remplacer après. */
export async function nextAg(club: string): Promise<string | null> {
  const { data } = await db().from('club_events').select('starts_at').eq('club_id', club).eq('kind', 'ag').is('cancelled_at', null)
    .gte('starts_at', new Date().toISOString()).order('starts_at').limit(1);
  return (data?.[0] as { starts_at: string } | undefined)?.starts_at ?? null;
}

// --- Coffre ------------------------------------------------------------------------------------------
const DOC_COLS = 'id, kind, title, note, doc_date, expires_on, held_outside, file_name, file_mime, file_size, file_at';
export const docs = (club: string) => rows<GrantDoc>(db().from('grant_docs').select(DOC_COLS).eq('club_id', club).order('created_at'));
export type DocDraft = Partial<Pick<GrantDoc, 'kind' | 'title' | 'note' | 'doc_date' | 'expires_on' | 'held_outside'>>;
export const saveDoc = (club: string, id: string | null, d: DocDraft) =>
  id ? one<GrantDoc>(db().from('grant_docs').update(d).eq('id', id).select(DOC_COLS).single())
    : one<GrantDoc>(db().from('grant_docs').insert({ club_id: club, ...d }).select(DOC_COLS).single());
export const removeDoc = (id: string) => must(db().from('grant_docs').delete().eq('id', id));
/** Déposer le fichier d'une pièce (remplace le précédent). La base vérifie taille et type. */
export const putFile = (club: string, docId: string, f: { name: string; mime: string; size: number; data: string }) =>
  must(db().from('grant_files').upsert({ doc_id: docId, club_id: club, ...f }, { onConflict: 'doc_id' }));
export const removeFile = (docId: string) => must(db().from('grant_files').delete().eq('doc_id', docId));
export const getFile = (docId: string) =>
  one<{ name: string; mime: string; data: string } | null>(db().from('grant_files').select('name, mime, data').eq('doc_id', docId).maybeSingle());

export const profile = (club: string) => one<GrantProfile | null>(db().from('grant_profile').select('*').eq('club_id', club).maybeSingle());
export const saveProfile = (club: string, d: Partial<Omit<GrantProfile, 'club_id'>>) =>
  one<GrantProfile>(db().from('grant_profile').upsert({ club_id: club, ...d }, { onConflict: 'club_id' }).select('*').single());

// --- Chiffres --------------------------------------------------------------------------------------------
export const figures = (club: string, season: string | null = null) =>
  rows<Figure>(db().rpc('club_figures', { p_club: club, p_season: season })).then(l => l.map(f => ({
    ...f, value: f.value === null ? null : Number(f.value), arceau_value: f.arceau_value === null ? null : Number(f.arceau_value),
    arceau_was: f.arceau_was === null ? null : Number(f.arceau_was),
  })));
export const saveFigure = (club: string, season: string, key: FigureKey, value: number | null, note: string | null, shared = true) =>
  must(db().rpc('grant_figure_save', { p_club: club, p_season: season, p_key: key, p_value: value, p_note: note, p_shared: shared }));
export const confirmFigure = (club: string, season: string, key: FigureKey) =>
  must(db().rpc('grant_figure_save', { p_club: club, p_season: season, p_key: key, p_confirm: true }));
export const resetFigure = (club: string, season: string, key: FigureKey) =>
  must(db().from('grant_figures').delete().eq('club_id', club).eq('season', season).eq('key', key));
export async function measures(club: string, season: string, inds: Indicator[]): Promise<Measure[]> {
  if (!inds.length) return [];
  const { data, error } = await db().rpc('grant_measures', { p_club: club, p_season: season, p_specs: inds.map(measureSpec) });
  if (error) throw new Error(error.message);
  return ((data as Measure[] | null) ?? []).map(m => ({ ...m, value: m.value === null ? null : Number(m.value) }));
}

// --- Dossiers ----------------------------------------------------------------------------------------------
export const applications = (club: string) => rows<Application>(db().from('grant_applications').select('*').eq('club_id', club).order('year', { ascending: false }).order('created_at'));
export const application = (id: string) => one<Application | null>(db().from('grant_applications').select('*').eq('id', id).maybeSingle());
export type AppDraft = Partial<Omit<Application, 'id' | 'created_at' | 'updated_at'>>;
export const createApplication = (club: string, d: AppDraft) => one<Application>(db().from('grant_applications').insert({ club_id: club, ...d }).select('*').single());
export const saveApplication = (id: string, d: AppDraft) => one<Application>(db().from('grant_applications').update(d).eq('id', id).select('*').single());
export const removeApplication = (id: string) => must(db().from('grant_applications').delete().eq('id', id));
export type { Budget };
